import { getContext, setContext } from "svelte";
import { Readable, Writable, readable, writable, derived, get } from "svelte/store";

const FORM_CONTEXT_KEY = {};

type FieldValueError = {
  message: string;
  name?: string;
} & Record<string, any>;

type ErrorMatcher = false | true | string | FieldValueError;

/***
 * A helper for generating errors
 *
 *
 * $: $field.errors = errorsIf(
 *    !$field.value && "is required",
 *    $field.value.length > 60 && "is too long",
 *    !isUrl($field.value) && {message: "not a valid url", url: $field.value}
 * )
 *
 * $field.errors = errorsIf(!$field.value) => will default to "is invalid" message
 */
export function errorsIf(...matchers: ErrorMatcher[]): FieldValueError[] {
  return matchers
    .filter((e) => e)
    .map((matcher) => {
      if (matcher === true) {
        return { message: "is invalid" };
      }

      return typeof matcher === "string" ? { message: matcher } : matcher;
    }) as FieldValueError[];
}

/**
 *
 *
 */
export function formField<T>(key: string, name?: string): FormFieldStore<T> {
  const { fields, cancelForm, submitForm, isDisabled } = getContext(FORM_CONTEXT_KEY);

  let fieldResult: FormFieldStore<T> | undefined = undefined;

  // Update the store and create a new field / return the active one
  fields.update((currentFields: { [id: string]: FormFieldStore<any> }) => {
    if (key in currentFields) {
      fieldResult = currentFields[key];
    } else {
      fieldResult = currentFields[key] = createFieldStore<T>(key, name, null, null, submitForm, cancelForm, isDisabled);
    }

    return currentFields;
  });

  if (!fieldResult) {
    throw new Error(`Can't setup field ${key}`);
  }

  return fieldResult;
}

type FormFieldStore<T> = Writable<FieldValue<T> & { isDisabled: boolean; isDirty: boolean }> & {
  submitForm: () => void;
  cancelForm: () => void;
};

function createFieldStore<T>(
  key: string,
  name: string | undefined,
  value: T | null,
  originalValue: T | null,
  submitForm: () => void,
  cancelForm: () => void,
  isDisabled: Readable<boolean>
): FormFieldStore<T> {
  // Else we create a new value
  const newField = writable<FieldValue<T>>({
    value,
    originalValue,
    isPending: false,
    errors: [],
    key,
    name
  });

  const fieldWithDisabled = derived<
    FieldValue<T> & { isDisabled: boolean; isDirty: boolean },
    [Writable<FieldValue<T>>, Readable<boolean>]
  >([newField, isDisabled], ([$newField, $isDisabled]) => {
    return {
      ...$newField,
      isDisabled: $isDisabled,
      isDirty: isFieldValueDirty($newField.value, $newField.originalValue)
    };
  });

  return {
    update: newField.update as (updater: Updater<FieldValue<T> & { isDisabled: boolean; isDirty: boolean }>) => void,
    set: newField.set,
    subscribe: fieldWithDisabled.subscribe,
    submitForm,
    cancelForm
  };
}

type Updater<T> = (value: T) => T;

interface FieldValue<TValue> {
  name?: string;
  value: TValue | undefined;
  originalValue: TValue | undefined;
  errors: Array<FieldValueError | null>;
  key: string;
  isPending: boolean;
}

type FieldsStore = Writable<{
  [id: string]: FormFieldStore<any>;
}>;

const DEFAULT_FORM_STATUS: FormStatus = { data: {}, isDirty: false, isPending: false, isValid: false, errors: [] };

export function setupForm(
  data: Record<string, any> | null,
  stagedData: Record<string, any> | undefined,
  submitForm: () => void,
  cancelForm: () => void,
  isDisabled: Writable<boolean>
) {
  // setup our fields
  const fields: FieldsStore = writable(setupFields(data, stagedData, isDisabled, submitForm, cancelForm));

  // set the context
  setContext(FORM_CONTEXT_KEY, { fields, submitForm, cancelForm, isDisabled });
  /*
    formValueStore is a map (key -> FormValueStore(key))

    Because formValues can only be added, when we have a new one, we need to subscribe to our update method.
  */

  const formStatusStore = readable<FormStatus>(DEFAULT_FORM_STATUS, (set) => {
    let unsubscribe: () => void;

    fields.subscribe((keyedFormFields) => {
      if (unsubscribe) {
        unsubscribe();
      }

      const listOfFields = Object.values(keyedFormFields) as [FormFieldStore<any>];

      const derivedStore = derived(listOfFields, calculateFormStatus);

      unsubscribe = derivedStore.subscribe(set);
    });

    // Cleanup function
    return () => {
      unsubscribe && unsubscribe();
    };
  });

  function reset() {
    // Get current fields
    const currentFields = get(fields);

    const listOfFields = Object.values(currentFields) as [Writable<FieldValue<any>>];

    // For each of them,
    listOfFields.forEach((fieldValueStore) => {
      // Set to original value + clear errors
      fieldValueStore.update((fieldValue) => {
        fieldValue.value = fieldValue.originalValue;
        fieldValue.isPending = false;
        fieldValue.errors = [];

        return fieldValue;
      });
    });
  }

  return {
    subscribe: formStatusStore.subscribe,
    reset
  };
}

export function setupGroupField<T = any>(key: string, name?: string, errorsIf?: (data: T) => FieldValueError[]) {
  const fieldStore = formField<T>(key, name);

  const fieldValue = get(fieldStore) as FieldValue<T>;

  // Get is disabled
  const { isDisabled } = getContext(FORM_CONTEXT_KEY);

  // We calculate
  const stagedData = fieldValue.value === fieldValue.originalValue ? undefined : fieldValue.value || undefined;

  const formStore = setupForm(fieldValue.originalValue, stagedData, fieldStore.submitForm, fieldStore.cancelForm, isDisabled);

  return readable(DEFAULT_FORM_STATUS, (set) => {
    const unsubscribe = formStore.subscribe((formStatus) => {
      // Here we need to update the parent field when the form store is changing

      fieldStore.update((currentFieldValue) => {
        return {
          ...currentFieldValue,
          value: formStatus.data as T,
          errors: [...(errorsIf ? errorsIf(formStatus.data as T) : []), ...formStatus.errors],
          isPending: formStatus.isPending
        };
      });

      set(formStatus);
    });

    return unsubscribe;
  });
}

interface FormStatus {
  data: Record<string, any>;
  isValid: boolean;
  isDirty: boolean;
  isPending: boolean;
  errors: FormFieldError[];
}

// Let's you define
type FormFieldError = FieldValueError & { key: string; name?: string };

function calculateFormStatus(fields: FieldValue<any>[]): FormStatus {
  const data: Record<string, any> = {};
  let isValid = true;
  let isPending = false;
  let isDirty = false;
  const errors: FormFieldError[] = [];
  fields.forEach((field) => {
    const fieldErrors = field.errors.filter((e) => e) as FieldValueError[];

    data[field.key] = field.value;
    // Append the errors
    fieldErrors.forEach((error) => {
      errors.push({
        name: field.name,
        key: field.key,
        ...error
      });
    });
    if (isValid && fieldErrors.length) {
      isValid = false;
    }

    if (!isPending && field.isPending) {
      isPending = true;
    }

    if (!isDirty && isFieldValueDirty(field.value, field.originalValue)) {
      isDirty = true;
    }
  });

  return {
    data: isNullObject(data) ? {} : data,
    isValid,
    isPending,
    errors,
    isDirty
  };
}

/*
  Compare field value changes with support for object keys comparison

  * for object fields, {} = null / undefined
  * {field: null, molsh: null, dolsh: null} and {}/null are equal for this function
  * object fields fallback to json deep compare
  

*/
function isFieldValueDirty(newValue: any, oldValue: any): boolean {
  if (newValue === oldValue) {
    return false;
  }

  // Since typeof null is "object" we'd like to consider this as well...
  if ((typeof newValue === "object" && newValue !== null) || (typeof oldValue === "object" && oldValue !== null)) {
    if (isNullObject(newValue) && isNullObject(oldValue)) {
      return false;
    }

    // if one is null
    if (!oldValue || !newValue) {
      return true;
    }

    // complicated case, just run json comparison
    return JSON.stringify(oldValue) !== JSON.stringify(newValue);
  }

  return true;
}

function isNullObject(input: Record<string, any>): boolean {
  if (!input) {
    return true;
  }

  const values = Object.values(input);
  return !values.length || values.every((v) => v === undefined || v === null);
}

function setupFields(
  data: Record<string, any> | null,
  stagedData: Record<string, any> | undefined,
  isDisabled: Readable<boolean>,
  submitForm: () => void,
  cancelForm: () => void
): Record<string, any> {
  const result: Record<string, Writable<FieldValue<any>>> = {};

  // Both empty null
  if (!data && !stagedData) {
    return result;
  }

  const stagedOrData = stagedData && Object.keys(stagedData).length ? stagedData : data || {};

  Object.keys(stagedOrData).forEach((key) => {
    result[key] = createFieldStore<any>(
      key,
      undefined,
      stagedOrData[key] || null,
      data ? data[key] || null : null, // Store original data here,
      submitForm,
      cancelForm,
      isDisabled
    );
  });

  return result;
}
